/* ==========================================================================
   Tribe & Trowel — Germination Protection (app-free in-cart upsell)
   Horizon ES module. FLAT-PRICED (2026-07-20): one variant, one price — no
   cart-size tiers, so the PDP quote and the cart line always match, and
   digital items can never change the price. The protection line is offered
   and kept only while the cart contains PHYSICAL merchandise
   (requires_shipping) — a digital-only cart never sees or keeps it.
   Reads window.TT_GUARANTEE (injected by snippets/tt-guarantee.liquid).
   After any change it dispatches Horizon's CartLinesUpdateEvent with
   freshly-rendered section HTML so the native cart re-renders.
   NOTE: the PDP buy-box checkbox sets the same tt_guarantee_optin key instead
   of adding the variant itself — this module is the single owner of the line.
   ========================================================================== */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const CFG = window.TT_GUARANTEE;
if (CFG && CFG.variants && CFG.variants.length) init();

function init() {
  const TIERS = CFG.variants.slice().sort((a, b) => a.price - b.price);
  const BOUNDS = (CFG.bounds && CFG.bounds.length === TIERS.length) ? CFG.bounds : [0];
  const IDS = {};
  TIERS.forEach((t) => { IDS[t.id] = true; });

  const OPTIN_KEY = 'tt_guarantee_optin';
  let busy = false, queued = false, selfMutating = false, lastPresent = false, lastBase = 0;

  const routes = () => { try { return Theme.routes || {}; } catch (e) { return {}; } };
  const CART_JS = () => (routes().cart_url || '/cart') + '.js';
  const ADD_JS = () => routes().cart_add_url || '/cart/add.js';
  const UPDATE_JS = () => (routes().cart_update_url || '/cart/update') + '.js';

  const getOptIn = () => { try { return localStorage.getItem(OPTIN_KEY) === '1'; } catch (e) { return false; } };
  const setOptIn = (v) => { try { localStorage.setItem(OPTIN_KEY, v ? '1' : '0'); } catch (e) {} };
  const money = (cents) => '$' + (cents / 100).toFixed(2);

  function pickTier(base) {
    let chosen = TIERS[0];
    for (let i = 0; i < TIERS.length; i++) if (base >= BOUNDS[i]) chosen = TIERS[i];
    return chosen;
  }

  const json = (url, opts) => fetch(url, Object.assign({ headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, credentials: 'same-origin' }, opts)).then((r) => r.json());
  const getCart = () => fetch(CART_JS(), { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((r) => r.json());

  function sectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => { if (el.dataset.sectionId) ids.add(el.dataset.sectionId); });
    return Array.from(ids).join(',');
  }

  const rawAdd = (id) => json(ADD_JS(), { method: 'POST', body: JSON.stringify({ items: [{ id, quantity: 1 }] }) });
  const rawUpdate = (updates) => json(UPDATE_JS(), { method: 'POST', body: JSON.stringify({ updates }) });
  // Full cart + freshly rendered section HTML (no-op update used purely to fetch sections).
  const fetchFull = () => json(UPDATE_JS(), { method: 'POST', body: JSON.stringify({ updates: {}, sections: sectionIds(), sections_url: location.pathname }) });

  function dispatchCartUpdate(full) {
    if (!full) return;
    try {
      const deferred = CartLinesUpdateEvent.createPromise();
      document.body.dispatchEvent(new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: deferred.promise }));
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(full),
        detail: { sections: full.sections, items: full.items, itemCount: full.item_count, source: 'tt-guarantee', didError: false },
      });
    } catch (e) {}
  }

  // Physical merchandise only: digital deliverables (requires_shipping=false)
  // and the protection line itself are excluded from the base.
  const baseOf = (cart) => ((cart && cart.items) || []).reduce((b, it) => b + ((IDS[it.variant_id] || !it.requires_shipping) ? 0 : it.final_line_price), 0);
  const lineOf = (cart) => ((cart && cart.items) || []).find((it) => IDS[it.variant_id]) || null;
  const zero = (vid) => ({ [vid]: 0 });
  const qtyOf = (vid, q) => ({ [vid]: q });

  async function reconcile() {
    if (busy) { queued = true; return; }
    busy = true;
    try {
      let cart = await getCart();
      const gLine = lineOf(cart);
      const present = !!gLine;
      const base = baseOf(cart);
      let optIn = getOptIn();

      // User manually removed the line while opted in -> respect that choice.
      if (!selfMutating && optIn && lastPresent && !present) { optIn = false; setOptIn(false); }

      const desired = pickTier(base);
      let mutated = false;
      selfMutating = true;
      try {
        if (optIn && base > 0) {
          if (!present) { await rawAdd(desired.id); mutated = true; }
          else if (gLine.variant_id !== desired.id) { await rawAdd(desired.id); await rawUpdate(zero(gLine.variant_id)); mutated = true; }
          else if (gLine.quantity !== 1) { await rawUpdate(qtyOf(gLine.variant_id, 1)); mutated = true; }
        } else if (present) {
          await rawUpdate(zero(gLine.variant_id)); mutated = true;
        }
        if (mutated) { const full = await fetchFull(); dispatchCartUpdate(full); cart = full; }
      } finally {
        selfMutating = false;
      }

      lastPresent = !!lineOf(cart);
      lastBase = baseOf(cart);
      render(lastBase);
    } catch (e) {
      /* swallow */
    } finally {
      busy = false;
      if (queued) { queued = false; setTimeout(reconcile, 40); }
    }
  }

  // ---------- UI ----------
  const SHIELD = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3l7 3v5c0 4.4-3 7.6-7 9-4-1.4-7-4.6-7-9V6l7-3z"/><path d="M9 12l2 2 4-4"/></svg>';
  const CHECK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12l4.5 4.5L19 7"/></svg>';

  function inner(price, optIn) {
    return (
      '<label class="tt-guar__main">' +
        '<input type="checkbox" class="tt-guar__input"' + (optIn ? ' checked' : '') + '>' +
        '<span class="tt-guar__check" aria-hidden="true">' + CHECK + '</span>' +
        '<span class="tt-guar__text">' +
          '<span class="tt-guar__title">' + SHIELD +
            '<span class="tt-guar__name">Germination Protection</span>' +
            '<span class="tt-guar__price">' + money(price) + '</span>' +
          '</span>' +
          '<span class="tt-guar__copy">If any variety fails to sprout, we replace it fast — no photos, no questions. Covers this order for a full 60 days.</span>' +
        '</span>' +
      '</label>'
    );
  }

  function anchors() {
    const list = [];
    document.querySelectorAll('.cart-summary__inner').forEach((el) => list.push(el));
    document.querySelectorAll('.cart-drawer__summary').forEach((el) => { if (!el.querySelector('.cart-summary__inner')) list.push(el); });
    return list;
  }

  function render(base) {
    const price = pickTier(base).price;
    const optIn = getOptIn();
    anchors().forEach((anchor) => {
      let card = anchor.querySelector(':scope > .tt-guar');
      if (base <= 0) { if (card && card.parentNode) card.parentNode.removeChild(card); return; }
      if (!card) {
        card = document.createElement('div');
        card.className = 'tt-guar';
        anchor.insertBefore(card, anchor.firstChild);
      }
      const sig = price + '|' + (optIn ? 1 : 0);
      if (card.getAttribute('data-sig') === sig) return;
      card.setAttribute('data-sig', sig);
      card.innerHTML = inner(price, optIn);
      const input = card.querySelector('.tt-guar__input');
      if (input) input.addEventListener('change', onToggle);
    });
  }

  function onToggle(e) { setOptIn(!!e.target.checked); reconcile(); }

  // ---------- change detection (native cart updates) ----------
  let rt;
  const schedule = () => { clearTimeout(rt); rt = setTimeout(reconcile, 120); };
  document.addEventListener(StandardEvents.cartLinesUpdate, (e) => {
    if (!e || !e.promise) { schedule(); return; }
    e.promise.then(({ detail }) => { if (!detail || detail.source !== 'tt-guarantee') schedule(); }).catch(() => {});
  });

  // Re-inject the card whenever Horizon morphs the cart section (which wipes it).
  let mo;
  const observe = () => {
    if (mo) return;
    mo = new MutationObserver(() => { clearTimeout(mo._t); mo._t = setTimeout(() => render(lastBase), 60); });
    mo.observe(document.body, { childList: true, subtree: true });
  };

  function boot() {
    observe();
    getCart().then((c) => {
      lastBase = baseOf(c);
      lastPresent = !!lineOf(c);
      render(lastBase);
      reconcile(); // re-assert opt-in state on every page load
    }).catch(() => {});
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
}
